<template>
  <div
    class="custom-multi-select"
    :class="{ 'is-open': isOpen, 'has-selections': selectedItems.length > 0 }"
  >
    <div class="select-trigger" @click="toggleDropdown" :class="{ 'is-focused': isOpen }">
      <div class="selected-items-display">
        <template v-if="selectedItems.length === 0">
          <span class="placeholder">{{ placeholder }}</span>
        </template>
        <template v-else>
          <div class="selected-tags">
            <span
              v-for="item in selectedItems.slice(0, 2)"
              :key="item.value"
              class="selected-tag"
            >
              {{ item.text }}
              <button type="button" class="remove-tag" @click.stop="removeItem(item)">
                ×
              </button>
            </span>
            <span v-if="selectedItems.length > 2" class="more-count">
              +{{ selectedItems.length - 2 }} more
            </span>
          </div>
        </template>
      </div>
      <div class="select-arrow">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>

    <div v-if="isOpen" class="custom-dropdown-menu">
      <div class="dropdown-header">
        <div class="search-container">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search options..."
            @input="filterOptions"
          />
        </div>
        <div class="select-all-container">
          <label class="select-all-checkbox">
            <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll" />
            <span class="checkmark"></span>
            Select All
          </label>
        </div>
      </div>

      <div class="options-list">
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="option-item"
          :class="{ 'is-selected': isSelected(option) }"
          @click="toggleOption(option)"
        >
          <label class="option-checkbox">
            <input
              type="checkbox"
              :checked="isSelected(option)"
              @change="toggleOption(option)"
            />
            <span class="checkmark"></span>
            <span class="option-text">{{ option.text }}</span>
          </label>
        </div>
      </div>

      <div v-if="filteredOptions.length === 0" class="no-options">No options found</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomMultiSelect',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Select options',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      filteredOptions: [],
    };
  },
  computed: {
    selectedItems() {
      return this.options.filter((option) => this.modelValue.includes(option.value));
    },
    isAllSelected() {
      return (
        this.filteredOptions.length > 0 &&
        this.filteredOptions.every((option) => this.isSelected(option))
      );
    },
  },
  watch: {
    options: {
      immediate: true,
      handler() {
        this.filteredOptions = [...this.options];
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },
    toggleOption(option) {
      const newValue = [...this.modelValue];
      const index = newValue.indexOf(option.value);

      if (index > -1) {
        newValue.splice(index, 1);
      } else {
        newValue.push(option.value);
      }

      this.$emit('update:model-value', newValue);
      this.$emit('change', newValue);
    },
    removeItem(item) {
      const newValue = this.modelValue.filter((value) => value !== item.value);
      this.$emit('update:model-value', newValue);
      this.$emit('change', newValue);
    },
    isSelected(option) {
      return this.modelValue.includes(option.value);
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        const newValue = this.modelValue.filter(
          (value) => !this.filteredOptions.some((option) => option.value === value)
        );
        this.$emit('update:model-value', newValue);
        this.$emit('change', newValue);
      } else {
        const newValue = [
          ...new Set([
            ...this.modelValue,
            ...this.filteredOptions.map((option) => option.value),
          ]),
        ];
        this.$emit('update:model-value', newValue);
        this.$emit('change', newValue);
      }
    },
    filterOptions() {
      if (!this.searchQuery.trim()) {
        this.filteredOptions = [...this.options];
      } else {
        this.filteredOptions = this.options.filter((option) =>
          option.text.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
label {
  margin-bottom: 0 !important;
}

.custom-multi-select {
  position: relative;
  width: 100%;
  font-family: 'Inter', sans-serif;

  .select-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 32px;
    padding: 6px 12px;
    background-color: #ffffff;
    border: 1px solid #303030;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &.is-focused {
      border-color: #00315a;
      box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
    }

    .selected-items-display {
      flex: 1;
      min-width: 0;

      .placeholder {
        color: #303030;
        font-size: 14px;
      }

      .selected-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        align-items: center;

        .selected-tag {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          background-color: #ddf4ff;
          color: #00315a;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;

          .remove-tag {
            background: none;
            border: none;
            color: #00315a;
            cursor: pointer;
            margin-left: 4px;
            padding: 0;
            font-size: 14px;
            line-height: 1;

            &:hover {
              color: #1a7f37;
            }
          }
        }

        .more-count {
          color: #656d76;
          font-size: 12px;
          font-weight: 500;
        }
      }
    }

    .select-arrow {
      color: #656d76;
      margin-left: 8px;
      transition: transform 0.2s ease;

      svg {
        width: 12px;
        height: 12px;
      }
    }
  }

  &.is-open .select-arrow {
    transform: rotate(180deg);
  }

  .custom-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    margin-top: 4px;
    background-color: #ffffff;
    border: 1px solid #303030;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(140, 149, 159, 0.2);
    max-height: 300px;
    overflow: hidden;

    .dropdown-header {
      padding: 12px;
      border-bottom: 1px solid #303030;

      .search-container {
        margin-bottom: 10px;

        .search-input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #303030;
          border-radius: 6px;
          font-size: 13px;
          outline: none;

          &:focus {
            border-color: #00315a;
            box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
          }

          &::placeholder {
            color: #656d76;
          }
        }
      }

      .select-all-container {
        .select-all-checkbox {
          display: flex;
          align-items: center;
          padding: 5px 0;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #24292f;

          input[type='checkbox'] {
            margin-right: 8px;
          }
        }
      }
    }

    .options-list {
      max-height: 200px;
      overflow-y: auto;

      .option-item {
        padding: 8px 12px;
        cursor: pointer;
        transition: background-color 0.15s ease;

        &:hover {
          background-color: #f6f8fa;
        }

        &.is-selected {
          background-color: #ddf4ff;
        }

        .option-checkbox {
          display: flex;
          padding: 6px 0;
          align-items: center;
          cursor: pointer;
          font-size: 14px;
          color: #24292f;

          input[type='checkbox'] {
            margin-right: 8px;
          }

          .option-text {
            flex: 1;
          }
        }
      }
    }

    .no-options {
      padding: 16px;
      text-align: center;
      color: #656d76;
      font-size: 14px;
    }
  }

  input[type='checkbox'] {
    appearance: none;
    width: 16px;
    height: 16px;
    border: 1px solid #d0d7de;
    border-radius: 3px;
    background-color: #ffffff;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;

    &:checked {
      background-color: #00315a;
      border-color: #00315a;

      &::after {
        content: '';
        position: absolute;
        left: 5px;
        top: 2px;
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }

    &:hover {
      border-color: #00315a;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
    }
  }

  &.has-selections .select-trigger {
    border-color: #00315a;
  }
}

@media (max-width: 768px) {
  .custom-multi-select {
    .select-trigger {
      .selected-items-display {
        .selected-tags {
          .selected-tag {
            font-size: 11px;
            padding: 1px 6px;
          }

          .more-count {
            font-size: 11px;
          }
        }
      }
    }
  }
}
</style>
